import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { RateOverrideInput, ServiceInput } from './types';

export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (params: api.ServiceListParams) => [...serviceKeys.lists(), params] as const,
  detail: (id: string | number) => [...serviceKeys.all, 'detail', id] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useServices(params: api.ServiceListParams) {
  return useQuery({
    queryKey: serviceKeys.list(params),
    queryFn: () => api.listServices(params),
    placeholderData: keepPreviousData,
  });
}

export function useService(id: string | number) {
  return useQuery({
    queryKey: serviceKeys.detail(id),
    queryFn: () => api.getService(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ServiceInput) => api.createService(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceKeys.lists() });
      toast.success('Service created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateService(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ServiceInput>) => api.updateService(id, input),
    onSuccess: (service) => {
      qc.invalidateQueries({ queryKey: serviceKeys.lists() });
      qc.setQueryData(serviceKeys.detail(id), service);
      toast.success('Service updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteService(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceKeys.lists() });
      toast.success('Service deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useAddRateOverride(serviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<RateOverrideInput, 'service'>) =>
      api.addRateOverride({ ...input, service: serviceId }),
    onSuccess: () => {
      // Overrides are nested in the service read — refetch the detail.
      qc.invalidateQueries({ queryKey: serviceKeys.detail(serviceId) });
      toast.success('Rate override added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}
